import { FC, useEffect, useRef } from 'react'

import { CheckOutlined } from '@mui/icons-material'
import { GRAY_900 } from 'constants/styling/theme'
import Typography from '@mui/material/Typography'
import styles from './CampaignGenerating.module.sass'
import { useTranslation } from 'react-i18next'

/** The five steps shown one after the other while the campaign is built (prototype v3 `STEPS`). */
export const GENERATION_STEPS = ['step_1', 'step_2', 'step_3', 'step_4', 'step_5'] as const

interface Props {
  /** Index of the step in progress; the ones before are done, `GENERATION_STEPS.length` once all are */
  step: number
}

/**
 * Generating (prototype v3 `genHtml`): the steps done get a check, the one in progress spins.
 * The Start button that led here goes away with the intro, so the title takes the focus.
 */
export const CampaignGenerating: FC<Props> = ({ step }) => {
  const { t } = useTranslation(['social_campaign'])
  const titleRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    titleRef.current?.focus()
  }, [])

  return (
    <section className={styles.generating}>

      <Typography
        ref={titleRef}
        variant="display-xs"
        component="h2"
        tabIndex={-1}
        // `&&` outranks the old-style routes' global h2 size and colour (src/styles/index.sass)
        sx={theme => ({ '&&': { margin: 0, fontSize: theme.typography['display-xs']?.fontSize, lineHeight: theme.typography['display-xs']?.lineHeight, color: GRAY_900, outline: 'none' } })}
      >
        {t('generating.title')}
      </Typography>

      <ol className={styles.steps}>
        {GENERATION_STEPS.map((key, index) => {
          const state = index < step ? 'done' : index === step ? 'live' : 'todo'
          return (
            <li key={key} className={styles.step} data-state={state}>
              <span className={styles.dot} aria-hidden="true">
                {state === 'done' && <CheckOutlined />}
              </span>
              {t(`generating.${key}`)}
              {state === 'done' && <span className={styles.srOnly}>, {t('generating.done')}</span>}
            </li>
          )
        })}
      </ol>

    </section>
  )
}

/**
 * What a screen reader hears of the generation: each step as it starts, the first one included. The tab
 * keeps this live region in the page before the generation starts, since a region inserted already
 * holding its text is not announced.
 */
export const CampaignGenerationAnnouncer: FC<{ step: number | null }> = ({ step }) => {
  const { t } = useTranslation(['social_campaign'])
  const current = step === null ? undefined : GENERATION_STEPS[step]

  return <p className={styles.srOnly} aria-live="polite">{current ? t(`generating.${current}`) : ''}</p>
}
